import type { Theme } from '@glideapps/glide-data-grid';

/**
 * The palette lives in `index.css` as custom properties on `:root`, because
 * that is the one place the cascade reaches everything. A canvas has no
 * cascade, so the grid asks the document element what those properties resolve
 * to and paints with the answers. Reading them here rather than keeping a
 * second copy of the values is the whole point: the grid cannot drift from the
 * DOM beside it.
 *
 * The values never change while the app is running, so the computed style is
 * fetched once and every property is remembered after its first read.
 */
let rootStyle: CSSStyleDeclaration | undefined;
const values = new Map<string, string>();

export function paletteValue(name: string): string {
  const remembered = values.get(name);
  if (remembered !== undefined) return remembered;
  rootStyle ??= getComputedStyle(document.documentElement);
  const value = rootStyle.getPropertyValue(name).trim();
  values.set(name, value);
  return value;
}

/**
 * Glide's own theme, in the same roles. It is built once, on first use: the
 * grid re-measures everything when its theme object changes identity, so a
 * fresh object per render would be a repaint per render.
 */
let theme: Partial<Theme> | undefined;

export function gridTheme(): Partial<Theme> {
  theme ??= {
    accentColor: paletteValue('--accent'),
    accentFg: paletteValue('--surface'),
    accentLight: paletteValue('--accent-soft'),
    textDark: paletteValue('--text'),
    textMedium: paletteValue('--text-muted'),
    textLight: paletteValue('--text-hint'),
    textBubble: paletteValue('--chip-text'),
    textHeader: paletteValue('--text-muted'),
    textHeaderSelected: paletteValue('--surface'),
    bgCell: paletteValue('--surface'),
    bgCellMedium: paletteValue('--bg'),
    bgHeader: paletteValue('--bg'),
    bgHeaderHovered: paletteValue('--hover'),
    bgHeaderHasFocus: paletteValue('--hover'),
    bgBubble: paletteValue('--chip-bg'),
    bgBubbleSelected: paletteValue('--accent-soft'),
    bgSearchResult: paletteValue('--attention-bg'),
    bgIconHeader: paletteValue('--text-muted'),
    fgIconHeader: paletteValue('--surface'),
    borderColor: paletteValue('--line'),
    horizontalBorderColor: paletteValue('--line'),
    drilldownBorder: paletteValue('--line-strong'),
    resizeIndicatorColor: paletteValue('--accent'),
    linkColor: paletteValue('--accent'),
  };
  return theme;
}
